export type Role = 'DESIGNER' | 'JUDGE' | 'ADMIN' | 'SUPER_ADMIN';

/**
 * Restores the Jotform form's Thai/Foreigner selector (docs/adr-be/0018-derived-portfolio-flag-and-nationality-type.md).
 * `null` means "never asked" — there is no default and no backfill. The backend prop itself is a
 * plain `string` validated via `@IsIn(['THAI','FOREIGN'])`, not a TS enum, deliberately: a third
 * value ("stateless / no documents") is a possible future DTO change, not a schema migration. Typed
 * as a union here anyway for the value-label mapping on the two profile pages — render anything
 * unrecognized through the same `common.empty` fallback as `null`, don't assume this union is
 * exhaustive against the wire.
 */
export type NationalityType = 'THAI' | 'FOREIGN';

/**
 * A resolved profile file — 24h signed URL (docs/adr-be/0017-profile-file-access.md). `mimeType`
 * is either `application/pdf` or an `image/*` type; the reader branches render on it (inline `<img>`
 * vs. a download/view link) rather than guessing from the filename.
 */
export interface ProfileFile {
  _id: string;
  url: string;
  mimeType: string;
}

/**
 * The Designer application-of-record subdoc, present only when `role === 'DESIGNER'`
 * (docs/adr-be/0016-embedded-designer-profile.md, widened by 0017 for file access). Never includes
 * `idDocumentImageId`/`portfolioFileId` (the raw refs) or a resolved `idDocument` — `GET
 * /auth/profile` never returns the ID document to the Designer who owns it, even as a URL
 * (docs/adr-be/0017); `hasIdDocument` is the only signal this route ever gives about it. Add
 * `idDocument` here only once an Admin-facing surface actually reads `GET /users/:id/profile`,
 * which is the one route that resolves it.
 *
 * `email` and `dateOfBirth` live on `User` itself, not here (`dateOfBirth` is the seed-credential
 * basis, admin-only per ADR-BE 0015/0016). Every free-text field below is free text on the
 * backend — no closed enums (ADR-BE 0016's "no @Prop({enum}) anywhere" decision) — so this
 * interface mirrors that: `string | null` where the backend can return an unset value, not a union
 * of known values. `nationalityType` is the one exception — see `NationalityType`'s doc comment.
 *
 * `hasPortfolio` is read-only/derived as of ADR-BE 0018 — `true` whenever `portfolioLink` or
 * `portfolioFile` is present, `false` otherwise. It is never accepted on `PATCH /users/me` (not in
 * `UpdateMyProfileRequest` below); setting it from a form control is a guaranteed `400`.
 * `nationalityType`/`nationality` are Admin-only, alongside `dateOfBirth` on `User` — same
 * identity-of-record rule, narrowed by ADR-BE 0018 from the wider group ADR-BE 0016 originally
 * used (`originCountry`/`region`/`province` moved to Designer-editable in that same amendment).
 */
export interface DesignerProfile {
  dateOfBirth: string | null;
  nationalityType: NationalityType | null;
  nationality: string | null;
  originCountry: string | null;
  region: string | null;
  province: string | null;
  gender: string | null;
  jobTitle: string | null;
  brand: string | null;
  phone: string | null;
  facebook: string | null;
  lineId: string | null;
  address: string | null;
  educationLevel: string | null;
  institution: string | null;
  hasPortfolio: boolean | null;
  portfolioLink: string | null;
  /** Resolved `portfolioFileId` — `null`/absent when never uploaded. */
  portfolioFile?: ProfileFile | null;
  /** The only signal this route gives about the ID document — never a URL, see class doc above. */
  hasIdDocument: boolean;
}

/**
 * The slim identity shape (`{ userId, email, role, fullName }`) shared by every user-returning
 * route. `GET /users/:id` returns it plus `phone` — see `UserLookup` below. Kept slim on purpose
 * even after Phase 9 widened the caller's-own-profile shape, so an Admin resolving a `designerId`
 * elsewhere (a Submission, a ScoreCard) never incidentally pulls profile data, including a minor's
 * date of birth, as a side effect of an unrelated lookup (docs/adr-be/0016-embedded-designer-profile.md).
 */
export interface User {
  userId: string;
  email: string;
  role: Role;
  fullName: string;
}

/**
 * `GET /users/:id` (Admin, api_specification.md §10) — the slim `User` plus `phone` (the
 * Designer's `designerProfile.phone`; absent for non-Designers or when unset; docs/api-changelog.md,
 * 2026-10-12). The only profile field this route carries. Kept off `User` so `FullUserProfile`
 * (`GET /auth/profile`, which has no top-level `phone`) doesn't inherit it.
 */
export interface UserLookup extends User {
  phone?: string;
}

/**
 * The widened shape `GET /auth/profile` (self) and `GET /users/:id/profile` (Admin, out of scope
 * this phase) return — mirrors the backend's own `toFullUserProfile`/`toUserProfile` mapper split
 * (docs/adr-be/0016-embedded-designer-profile.md) rather than folding `mustChangePassword`/
 * `designerProfile` into the slim `User` above. This is what `AuthService.user` actually holds:
 * both `login()` and `loadProfile()` resolve this full shape, never the slim one.
 */
export interface FullUserProfile extends User {
  /**
   * `true` means this credential was issued by someone else (a DOB-seeded Designer password or a
   * generated provisioning temp password, docs/adr-be/0015) rather than self-chosen. The backend
   * does not enforce this on any route by design — see docs/adr/0014-*.md; this app is the sole
   * enforcement point.
   */
  mustChangePassword: boolean;
  /** Present only when `role === 'DESIGNER'`. */
  designerProfile?: DesignerProfile;
}

export interface Tokens {
  accessToken: string;
  refreshToken: string;
}

/**
 * `mustChangePassword` is flat here, same as on `FullUserProfile` — but `designerProfile` is not:
 * login/signup responses were never widened with it (only `GET /auth/profile` was), so a fresh
 * login's `User` signal won't carry `designerProfile` until the profile page's own `rxResource`
 * fetches it.
 */
export interface LoginResponse extends Omit<FullUserProfile, 'designerProfile'> {
  accessToken: string;
  refreshToken: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface ChangePasswordResponse {
  changed: true;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResponse {
  requested: true;
}

export interface ResetPasswordRequest {
  email: string;
  token: string;
  newPassword: string;
}

export interface ResetPasswordResponse {
  reset: true;
}

/**
 * Body of `PATCH /users/me` (api_specification.md §10) — the Designer's own lenient partial edit.
 * `fullName` applies to any role; every other field is Designer-only and `400`s if sent by a
 * Judge/Admin/SuperAdmin. `email`, `dateOfBirth`, `nationalityType`, `nationality` are deliberately
 * absent — identity-of-record, Admin-only (ADR-BE 0018). `hasPortfolio` is likewise deliberately
 * absent — it's derived from `portfolioLink`/an uploaded file, never stored (ADR-BE 0018); change
 * what it reflects via `portfolioLink` here or the `POST`/`DELETE /users/me/portfolio-file` routes.
 * The route's pipe adds `forbidNonWhitelisted`, so sending any field not listed below 400s rather
 * than silently no-op'ing. `originCountry`/`region`/`province` moved here from Admin-only in the
 * same amendment — nothing in the backend gates on them.
 */
export interface UpdateMyProfileRequest {
  fullName?: string;
  gender?: string;
  jobTitle?: string;
  brand?: string;
  phone?: string;
  facebook?: string;
  lineId?: string;
  address?: string;
  educationLevel?: string;
  institution?: string;
  portfolioLink?: string;
  originCountry?: string;
  region?: string;
  province?: string;
}

/**
 * Body of `POST /auth/provision` (api_specification.md §2:251-285, docs/adr-be/0015). Creates a
 * Judge/Admin/SuperAdmin account only — Designer accounts come from `/auth/signup` (self-service)
 * or `POST /users/designers` (Admin, imported/manual create) instead. The route's pipe has
 * `forbidNonWhitelisted`, so sending any field beyond these three 400s the whole call.
 */
export type ProvisionableRole = Exclude<Role, 'DESIGNER'>;

export interface ProvisionUserRequest {
  email: string;
  fullName: string;
  role: ProvisionableRole;
}

/**
 * `201` response of `POST /auth/provision`. Deliberately **not** `User` — the field is `id`, not
 * `userId`, and this shape carries a one-time `temporaryPassword` that `User`/`FullUserProfile`
 * never do.
 */
export interface ProvisionUserResponse {
  id: string;
  email: string;
  role: ProvisionableRole;
  fullName: string;
  /**
   * Server-generated, returned exactly once — there is no route that can retrieve it again. Hand
   * it to the new account holder out-of-band; they rotate it via the forced `mustChangePassword`
   * flow below on first login.
   */
  temporaryPassword: string;
  /** The *new* account's flag, not the acting SuperAdmin's — unrelated to mustChangePasswordGuard
   * on this page (it's always `true` for a freshly provisioned account). */
  mustChangePassword: boolean;
}

export const AUTH_ERROR_CODES = {
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  TOKEN_INVALID: 'TOKEN_INVALID',
  REFRESH_TOKEN_EXPIRED: 'REFRESH_TOKEN_EXPIRED',
  REFRESH_TOKEN_INVALID: 'REFRESH_TOKEN_INVALID',
  REFRESH_TOKEN_ROTATED: 'REFRESH_TOKEN_ROTATED',
  UNAUTHORIZED: 'UNAUTHORIZED',
} as const;

export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[keyof typeof AUTH_ERROR_CODES];
